import { useEffect, useMemo, useState } from 'react';
import { A_PAGE, at } from '../paging';
import { PageSize, Pages } from './Pages';
import { accounts, type AuthProblem } from '../backend';
import { currentWorkspace } from '../workspace';
import { useApp, readOpenTo, writeOpenTo, type OpenTo } from '../store';
import { ROLE_LABEL, ROLE_BLURB } from '../permissions';
import { displayName, initials, joinName, splitName } from '../people';
import VendorTag from './VendorTag';
import { EULA_VERSION, eulaClauses } from '../eula';
import { historyTime } from '../utils';
import { askPermission, permission, NOTIFY_LABEL, NOTIFY_BLURB, type NotifyWhen } from '../notify';
import { AUDIT_LABEL, type AuditEntry } from '../audit';
import { deviceId, orderSessions, type Session } from '../sessions';
import Icon from './Icon';
import WhenAway from './WhenAway';

/**
 * Your own account — a screen, not a window over the board.
 *
 * IT HAS BEEN BOTH, AND THIS IS THE THIRD SHAPE. It began as a dropdown five
 * pixels wider than the sidebar, which was right while it held two things. It
 * became a window when the terms would not fit in a strip that narrow. It is a
 * screen now, for the same reason People and access is one: this is somewhere
 * you go and spend a minute, not something you glance at and dismiss.
 *
 * A window over the board also lies about where you are — the board is still
 * behind it, still the thing the sidebar says you are looking at. A screen says
 * plainly that you have gone somewhere, and the way back is the way back from
 * anywhere else.
 *
 * Your name lives here rather than in an admin screen because it is yours: the
 * rules let anybody write their own profile and nobody else's, so a reviewer
 * can fix how their name reads on their own comments without asking permission.
 */

type Section = 'you' | 'security' | 'projects' | 'away' | 'devices' | 'activity' | 'notifications' | 'organisation' | 'terms';

/*
 * NAMED AS THE THINGS THEY ARE.
 *
 * These read "Boards you can open", "Where you are signed in", "Your security
 * activity" — descriptions of what you would find rather than names for it.
 * That is how a settings page in a toy reads, and this one has to sit in front
 * of somebody's IT department.
 *
 * Two of them also shared a glyph, so a menu with two lists of places one
 * above the other gave the eye nothing to tell them apart.
 */
/* `reveal` is the deep-link id a help answer or ⌘K drills to (drillTo('account',
   reveal)); we open on that section instead of the default Profile, and flash the
   nav row — the account-screen twin of Settings' REVEAL_TAB. */
const SECTIONS: { id: Section; label: string; icon: 'pencil' | 'bell' | 'check' | 'users' | 'board' | 'device' | 'briefcase'; reveal?: string }[] = [
  { id: 'you', label: 'Profile', icon: 'pencil', reveal: 'profile' },
  { id: 'security', label: 'Sign-In and Security', icon: 'check', reveal: 'password' },
  { id: 'projects', label: 'Projects', icon: 'board' },
  /* Nominate who signs your sign-offs while you are on leave — configurable
     sign-offs R1 (0130). Sits by Projects because it is about your work across
     them, not your identity or your devices. */
  { id: 'away', label: "When I'm away", icon: 'briefcase', reveal: 'cover' },
  { id: 'devices', label: 'Devices', icon: 'device', reveal: 'devices' },
  { id: 'activity', label: 'Security Activity', icon: 'bell', reveal: 'security-activity' },
  { id: 'notifications', label: 'Notifications', icon: 'bell', reveal: 'notify-prefs' },
  /*
   * "ORGANISATION" UNTIL 12 AUGUST. A third word for a thing already called a
   * workspace in the switcher and a tenant in the database — and the one a
   * person actually reads. One word in front of people, whatever the schema
   * calls it.
   */
  { id: 'organisation', label: 'Workspaces', icon: 'users', reveal: 'workspaces' },
  { id: 'terms', label: 'Terms of Use', icon: 'check' },
];

export default function Account() {
  const user = useApp((s) => s.user);
  /*
   * YOUR REAL ROLE, not a previewed one.
   *
   * This is your own account screen, and a preview is about what somebody ELSE would see. Reading
   * the previewed role here put MY name above THEIR role — "Vinnie Narace ·
   * Reviewer" while previewing a reviewer — which is a screen telling somebody
   * their own rights have changed. They have not: every read and write still
   * goes out as them. See src/viewas.ts.
   */
  const role = useApp((s) => s.role);
  const profiles = useApp((s) => s.profiles);
  const setPersonProfile = useApp((s) => s.setPersonProfile);
  const signOutUser = useApp((s) => s.signOutUser);
  const [perm, setPerm] = useState(permission());
  /* A help answer or ⌘K can deep-link to a section (drillTo('account', reveal));
     open on it rather than the default Profile, on first render and on a later
     drill. App.tsx then flashes the matching nav row's [data-reveal]. */
  const revealId = useApp((s) => s.revealId);
  const [section, setSection] = useState<Section>(
    () => SECTIONS.find((s) => s.reveal && s.reveal === revealId)?.id ?? 'you'
  );
  useEffect(() => {
    const hit = SECTIONS.find((s) => s.reveal && s.reveal === revealId);
    if (hit) setSection(hit.id);
  }, [revealId]);
  /* Where FlightDeck opens for you (#457) — a device-local choice, so it is held
     in local storage and read straight from there, not from the server profile. */
  const [openTo, setOpenTo] = useState<OpenTo>(readOpenTo);
  /*
   * The workspaces this person is in, for the Workspaces panel.
   *
   * Fetched only when that panel is opened rather than on every visit to the
   * account screen: it is one of eight sections, seven of which never need it,
   * and it cannot come from an ordinary read — see workspaces() on the
   * interface for why asking is a function call rather than a query.
   */
  const [myWorkspaces, setMyWorkspaces] = useState<{ id: string; name: string; isAdmin: boolean }[]>([]);
  const [hereNow, setHereNow] = useState('');
  // The workspace's currency and exchange rates moved to the Workspace hub's
  // Settings tab (src/components/WorkspaceSettings.tsx) — a tenant-wide setting
  // belongs with the workspace, not on a person's Account.

  useEffect(() => {
    // The Workspaces panel needs the whole list; the "When I'm away" panel needs
    // only the current workspace's NAME (for "cover applies across …"), but it
    // comes from the same one call, so both sections share this fetch.
    if (section !== 'organisation' && section !== 'away') return;
    let stop = false;
    accounts
      .workspaces()
      .then((list) => {
        if (stop) return;
        setMyWorkspaces(list);
        setHereNow(currentWorkspace() || list[0]?.id || '');
      })
      // Nothing to show and nothing to claim. The panel's own empty wording is
      // honest either way, and inventing a workspace here would be worse.
      .catch(() => setMyWorkspaces([]));
    return () => {
      stop = true;
    };
  }, [section]);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwState, setPwState] = useState<'' | 'saving' | 'done'>('');
  const [pwTrouble, setPwTrouble] = useState('');
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const sessions = useApp((s) => s.sessions);
  const sessionsProblem = useApp((s) => s.sessionsProblem);
  const signOutEverywhere = useApp((s) => s.signOutEverywhere);
  const endSession = useApp((s) => s.endSession);
  const audit = useApp((s) => s.audit);
  const auditProblem = useApp((s) => s.auditProblem);
  const [ending, setEnding] = useState(false);

  /**
   * Setting or changing the password on this account.
   *
   * BOTH, WITH ONE CONTROL. Somebody who has only ever used an emailed link
   * has no password, and telling them apart from somebody who has is not
   * something the browser can do reliably — an emailed link and a password are
   * the same provider to Firebase. So this sets one either way, which is the
   * right outcome in both cases.
   *
   * A password is not required to use this board. It is an alternative to the
   * emailed link for people who prefer it, and that is said rather than
   * implied, so nobody sets one believing they had to.
   */
  const changePassword = () => {
    setPwTrouble('');
    if (pw.length < 8) return setPwTrouble('Use at least 8 characters.');
    if (pw !== pw2) return setPwTrouble('The two passwords are not the same.');
    setPwState('saving');
    void accounts.setPassword(pw).then(
      () => {
        setPwState('done');
        setPw('');
        setPw2('');
      },
      (e: unknown) => {
        setPwState('');
        /*
         * The one failure worth explaining. Changing a password on a session
         * that has been open a while is refused, which is correct — it stops
         * somebody changing it at a machine you walked away from. Shown raw it
         * reads as a fault in the board.
         *
         * Asked of the boundary by REASON rather than by matching a Firebase
         * code, so this message survives a change of provider. See
         * src/backend/auth.ts.
         */
        const why = (e as { reason?: AuthProblem; code?: string });
        setPwTrouble(
          why.reason === 'needs-recent-sign-in'
            ? 'For your safety this needs a fresh sign-in. Sign out, sign in again, and change it within a few minutes.'
            : why.reason === 'weak-password'
              ? 'That password is too easily guessed. Try a longer one.'
              : why.code || String(e)
        );
      }
    );
  };

  const email = (user?.email ?? '').toLowerCase();
  const me = profiles[email] ?? {};
  /*
   * Only what is about this person, newest first. The trail holds everybody's,
   * and an admin reading their own account should see their own activity here
   * rather than the whole workspace's — People and access is where that lives.
   *
   * ── NOT CUT TO FIFTY HERE ANY MORE — #294 ─────────────────────────────────
   *
   * It used to end `.slice(0, 50)`, silently. On an address with 800 entries
   * that is 750 of somebody's own audit trail dropped without a word, on the
   * one screen whose job is to let them check it. The panel now decides how
   * many to DRAW, says how many there are, and offers the rest — which is a
   * different thing from the list not containing them.
   *
   * Sorted here rather than relied upon: `audit` arrives newest-first from the
   * store, and a screen that would be wrong if that ever changed should not be
   * quietly depending on it.
   */
  const mine = useMemo(
    () => audit.filter((e) => e.who === email).sort((a, b) => b.at - a.at),
    [audit, email]
  );

  return (
    <div className="settings account-screen">
      <div className="acc-top">
        <span className="avatar-me acc-av">{initials(email, profiles)}</span>
        <div className="acc-id">
          <div className="acc-name">{displayName(email, profiles)}<VendorTag who={email} profiles={profiles} compact /></div>
          <div className="acc-email">{email}</div>
        </div>
        {/* YOUR ROLE ON THE BOARD YOU ARE IN, and it is meant to change as you
            move between them. It does not yet — a role is still a workspace
            thing — which is said in the Boards section rather than here, where
            a caveat would crowd out the fact. */}
        <span className={`role-tag role-${role}`}>{ROLE_LABEL[role]}</span>
      </div>

      <div className="acc-body">
        <nav className="acc-nav" aria-label="Account sections">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-reveal={s.reveal}
              className={`acc-nav-item ${section === s.id ? 'active' : ''}`}
              onClick={() => setSection(s.id)}
            >
              <Icon name={s.icon} size={14} /> {s.label}
            </button>
          ))}
        </nav>

        <section className="panel acc-main">
          {section === 'you' && (
            <>
              <h2>Profile</h2>
              {/* TWO FIELDS, because a given name and a family name are two
                  facts. They are sorted, addressed and matched separately, and
                  no rule recovers them from one string — "Van der Berg" is one
                  surname, "Mary Jane" is one given name.

                  `name` is still written, as the joined form, so everything
                  that shows a person keeps reading one field. */}
              <div className="acc-pair">
                <label className="stack">
                  First name
                  <input
                    className="input"
                    defaultValue={splitName(me.firstName ? joinName(me.firstName, me.lastName) : me.name).first}
                    onBlur={(e) => {
                      const first = e.target.value.trim();
                      void setPersonProfile(email, {
                        firstName: first,
                        name: joinName(first, me.lastName ?? splitName(me.name).last),
                      });
                    }}
                  />
                </label>
                <label className="stack">
                  Last name
                  <input
                    className="input"
                    defaultValue={splitName(me.firstName ? joinName(me.firstName, me.lastName) : me.name).last}
                    onBlur={(e) => {
                      const last = e.target.value.trim();
                      void setPersonProfile(email, {
                        lastName: last,
                        name: joinName(me.firstName ?? splitName(me.name).first, last),
                      });
                    }}
                  />
                </label>
              </div>
              <p className="hint">
                Shown as <strong>{displayName(email, profiles)}</strong> on your comments.
              </p>
              <label className="stack">
                Job title
                <input
                  className="input"
                  defaultValue={me.title ?? ''}
                  placeholder="e.g. Designer, or Advising on the API"
                  onBlur={(e) => void setPersonProfile(email, { title: e.target.value.trim() })}
                />
              </label>
              <p className="hint">
                You are {/^[aeiou]/.test(ROLE_LABEL[role].toLowerCase()) ? 'an' : 'a'}{' '}
                <strong>{ROLE_LABEL[role].toLowerCase()}</strong> here.{' '}
                {ROLE_BLURB[role]}
              </p>
              {/* WHERE THE APP OPENS — #457. My Work is the default landing (your
                  whole queue the moment you sign in); people who live on one board,
                  or who want to pick up where they left off, change it here. Device-
                  local like the appearance setting, so the hint says so. */}
              <label className="stack">
                Open to
                <select
                  value={openTo}
                  onChange={(e) => {
                    const choice = e.target.value as OpenTo;
                    writeOpenTo(choice);
                    setOpenTo(choice);
                  }}
                >
                  <option value="mywork">My work</option>
                  <option value="board">The board</option>
                  <option value="resume">Where I left off</option>
                </select>
                <span className="hint">
                  The screen FlightDeck opens on this device when you sign in. Takes effect next
                  time you open the app.
                </span>
              </label>
              <div className="acc-actions">
                <button className="btn btn-danger" onClick={signOutUser}>
                  <Icon name="close" size={14} /> Sign out
                </button>
              </div>
            </>
          )}

          {section === 'security' && (
            <>
              <h2>Sign-In and Security</h2>
              <p className="hint">
                You sign in as <strong>{email}</strong>. That address is who you are here and
                cannot be changed — everything you have written is attached to it.
              </p>

              <label className="stack">
                New password
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                />
              </label>
              <label className="stack">
                And again
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                />
              </label>

              {pwTrouble && (
                <p className="acc-trouble" role="alert">
                  {pwTrouble}
                </p>
              )}
              {pwState === 'done' && <p className="acc-done">Your password has been changed.</p>}

              <div className="acc-actions">
                <button
                  className="btn btn-primary"
                  disabled={pwState === 'saving' || !pw || !pw2}
                  onClick={changePassword}
                >
                  {pwState === 'saving' ? 'Saving…' : 'Set password'}
                </button>
              </div>

              {/* Said plainly, so nobody sets one believing they had to. */}
              <p className="hint">
                A password is optional. You can always sign in with a link emailed to you instead,
                which is what most people here do.
              </p>
            </>
          )}

          {section === 'organisation' && (
            <>
              <h2>Workspaces</h2>
              {/*
                THIS PANEL SAID "you are not part of an organisation" UNTIL
                11 AUGUST, which stopped being true the day workspaces were
                built and was never noticed, because nothing here was reading
                anything — it was two paragraphs of copy describing a plan.
                A screen that describes the roadmap rather than the account is
                a screen nobody can tell has gone stale.

                Empty is still the honest answer on the Firebase copy, which
                has no notion of a workspace at all — see the interface.
              */}
              {myWorkspaces.length === 0 ? (
                <p className="hint">
                  This copy has one workspace and no way to express a second, so there is nothing to
                  choose between.
                </p>
              ) : (
                <>
                  <div className="proj-list">
                    {myWorkspaces.map((w) => (
                      <div key={w.id} className={`proj-row ${w.id === hereNow ? 'is-here' : ''}`}>
                        <div className="proj-main">
                          <div className="proj-name">
                            {w.name}
                            {w.id === hereNow && <span className="proj-here">Current</span>}
                          </div>
                          <div className="proj-sub">{w.isAdmin ? 'Admin' : 'Member'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="hint">
                    {myWorkspaces.length === 1
                      ? 'Everybody in this workspace shares its boards, its people and its history. Nothing here is visible from any other workspace, and nothing in another is visible from here.'
                      : 'Move between them from the name at the top of the sidebar. Nothing in one is visible from the other.'}
                  </p>
                </>
              )}
            </>
          )}

          {section === 'projects' && (
            <>
              <h2>Projects</h2>
              {projects.length === 0 ? (
                <p className="hint">No projects yet.</p>
              ) : (
                <div className="proj-list">
                  {projects.map((p) => (
                    <div key={p.id} className={`proj-row ${p.id === projectId ? 'is-here' : ''}`}>
                      <div className="proj-main">
                        <div className="proj-name">
                          {p.name}
                          {p.id === projectId && <span className="proj-here">Current</span>}
                        </div>
                        <div className="proj-sub">{p.key}</div>
                      </div>
                      <span className={`role-tag role-${role}`}>{ROLE_LABEL[role]}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* SAID, RATHER THAN LEFT TO LOOK LIKE A FAULT. Every row shows
                  the same role because a role is still workspace-wide. The
                  badge at the top is your role on the board you are in, and it
                  is built to change as you move between them — it just has
                  nothing to change to yet. */}
              <p className="hint">
                Your role is currently the same on every project, which is why each row reads alike.
              </p>
            </>
          )}

          {section === 'away' && (
            <WhenAway workspaceName={myWorkspaces.find((w) => w.id === hereNow)?.name ?? ''} />
          )}

          {section === 'devices' && (
            <DevicesPanel
              sessions={sessions}
              problem={sessionsProblem}
              thisDevice={deviceId()}
              ending={ending}
              onEnd={(id) => void endSession(id)}
              onEndEverywhere={() => {
                setEnding(true);
                void signOutEverywhere();
              }}
            />
          )}

          {section === 'activity' && <ActivityPanel entries={mine} problem={auditProblem} />}

          {section === 'notifications' && (
            <>
              <h2>Notifications</h2>
              <label className="stack">
                Tell me about
                <select
                  value={me.notifyWhen ?? 'all'}
                  onChange={(e) =>
                    void setPersonProfile(email, { notifyWhen: e.target.value as NotifyWhen })
                  }
                >
                  {(['all', 'mentions', 'none'] as NotifyWhen[]).map((w) => (
                    <option key={w} value={w}>
                      {NOTIFY_LABEL[w]}
                    </option>
                  ))}
                </select>
                <span className="hint">{NOTIFY_BLURB[me.notifyWhen ?? 'all']}</span>
              </label>

              {perm === 'default' && (
                <button className="btn btn-small" onClick={() => void askPermission().then(setPerm)}>
                  Allow notifications
                </button>
              )}
              {perm === 'denied' && (
                <p className="hint">
                  Your browser is blocking notifications for this site. Turn them back on in the
                  browser's settings for this page.
                </p>
              )}

              {/* Said here rather than discovered later. */}
              <p className="hint">
                These show while the board is open. When it is closed, unread call-outs are emailed
                to you instead.
              </p>

              <label className="stack">
                Send emails to <span className="hint">(the address we&rsquo;ll reach you at)</span>
                <input
                  className="input"
                  type="email"
                  placeholder={email}
                  defaultValue={me.notifyEmail ?? ''}
                  onBlur={(e) => void setPersonProfile(email, { notifyEmail: e.target.value.trim() })}
                />
                {/* Kept apart from the sign-in address on purpose: that one is
                    an identity, this is a preference. Plenty of people sign in
                    with a work address and read their post somewhere else. */}
                <span className="hint">
                  The address we&rsquo;ll email when you&rsquo;re away from the board. Leave it empty to
                  use {email}.
                </span>
              </label>

              {/* The away-email is its own switch so it can be off while the bell
                  stays on — #413. Default on; the digest also stops entirely when
                  "Tell me about" is None. Reuses the app's switch (see Settings). */}
              <div className={`feature-row ${(me.emailCallouts ?? true) ? 'is-on' : ''}`}>
                <button
                  className={`switch ${(me.emailCallouts ?? true) ? 'on' : ''}`}
                  role="switch"
                  aria-checked={me.emailCallouts ?? true}
                  aria-label="Email me when I'm away"
                  onClick={() =>
                    void setPersonProfile(email, { emailCallouts: !(me.emailCallouts ?? true) })
                  }
                >
                  <span className="switch-knob" />
                </button>
                <div className="feature-main">
                  <div className="feature-name">Email me when I&rsquo;m away</div>
                  <p className="feature-what">
                    When the board is closed, email me the call-outs I haven&rsquo;t seen — off keeps
                    them in the app only.
                  </p>
                </div>
              </div>

              {/* The per-notification switches (0126). "Tell me about" above sets
                  the overall level; these turn individual ones off. */}
              <div className="divider" />
              <p className="hint">
                &ldquo;Tell me about&rdquo; sets your overall level. Turn off any of these
                individually.
              </p>
              <NotificationSwitches />
            </>
          )}

          {/* Terms somebody agreed to and can never read again are terms they
              cannot check. This is why the account outgrew a dropdown. */}
          {section === 'terms' && (
            <>
              <h2>Terms of Use</h2>
              <p className="hint">
                {me.eulaVersion
                  ? `You accepted version ${me.eulaVersion}${
                      me.eulaAcceptedAt ? ` — ${historyTime(me.eulaAcceptedAt)}` : ''
                    }. The current version is ${EULA_VERSION}.`
                  : `You have not accepted these yet. The current version is ${EULA_VERSION}.`}
              </p>
              <div className="acc-terms">
                {eulaClauses().map((c) => (
                  <section key={c.heading}>
                    <h3>{c.heading}</h3>
                    {c.body.map((p, n) => (
                      <p key={n}>{p}</p>
                    ))}
                  </section>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * WHERE YOU ARE SIGNED IN — the drawing of it, with nothing fetched.
 *
 * SPLIT OUT SO IT CAN BE CHECKED AT ALL, which is not a stylistic preference.
 * This lives behind one of eight tabs, chosen by `useState` inside the screen
 * above, and the screen harness renders without a browser and cannot click. So
 * for as long as this markup sat inline, nothing in the suite could reach it —
 * and it showed: a button here has read `'Sign out'`, quote marks included,
 * since the day it was written, and the list it sits in has been empty since
 * the move to Postgres, so no person and no test ever saw the row draw.
 *
 * The three states are the whole point of the split, and they are genuinely
 * different answers rather than one answer with decoration:
 *
 *   · REFUSED — the list could not be read, so what you are signed in on is
 *     not known. Said plainly, because this is a security screen.
 *   · EMPTY — read successfully, and there is nothing on it yet.
 *   · A LIST — this browser first, then the rest by when they were last used.
 *
 * Until #289 the first two were the same screen, and the words it showed were
 * the reassuring ones.
 */
export function DevicesPanel({
  sessions,
  problem,
  thisDevice,
  ending,
  onEnd,
  onEndEverywhere,
}: {
  sessions: Session[];
  /** Empty when the list is trustworthy; otherwise why it is not. */
  problem: string;
  thisDevice: string;
  ending: boolean;
  onEnd: (id: string) => void;
  onEndEverywhere: () => void;
}) {
  return (
    <>
      <h2>Devices</h2>
            {problem ? (
              /*
               * THE REFUSAL, SAID PLAINLY. An empty list used to assert that
               * nothing had been recorded yet, whatever the reason — so a
               * query the database refused read as reassurance. This is the
               * one case where the screen genuinely does not know what you
               * are signed in on, and it now says so.
               */
              <p className="hint dev-problem">
                This list could not be read, so it is not safe to treat it as complete —{' '}
                {problem}. Try again in a moment; if it keeps happening, please report
                it.
              </p>
            ) : sessions.length === 0 ? (
              <p className="hint">Nothing recorded yet. This browser will appear here shortly.</p>
            ) : (
              <div className="dev-list">
                {orderSessions(sessions, thisDevice).map((d) => {
                  const here = d.id === thisDevice;
                  return (
                    <div key={d.id} className={`dev-row ${here ? 'is-here' : ''}`}>
                      <div className="dev-main">
                        <div className="dev-name">
                          {d.device}
                          {here && <span className="dev-here">This device</span>}
                        </div>
                        <div className="dev-sub">
                          {d.where} · last used {historyTime(d.lastSeen)}
                        </div>
                      </div>
                      <button
                        className="btn btn-small"
                        onClick={() => onEnd(d.id)}
                      >
                        {/* Written as 'Sign out' — quote marks and all — since the day
                            this was built. JSX text is not a string literal, so it drew
                            them. Nobody saw it because the list this sits in has been
                            empty ever since the move to Postgres: see noteThisDevice. */}
                        Sign out
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="acc-actions">
              <button
                className="btn btn-danger"
                disabled={ending}
                onClick={onEndEverywhere}
              >
                {ending ? 'Signing out…' : 'Sign out everywhere'}
              </button>
            </div>

            {/* THE LIMIT, SAID RATHER THAN IMPLIED. A browser cannot revoke a
                sign-in token by itself; that is the server's to do. A security
                screen that overstates what it did is worse than one that does
                less and says so. The old wording named Firebase, which this app
                has not run on since the move to Postgres — a vendor's name on a
                security screen should at least be the right vendor. */}
            <p className="hint">
              This lists the browsers this account has been opened in, recorded by the board
              itself. Signing out everywhere instructs each of them to sign out, and they do —
              which covers a laptop left elsewhere or a machine you no longer use. It cannot
              reach a browser that isn&rsquo;t currently connected to FlightDeck — for example, one
              that is fully closed.
            </p>
    </>
  );
}

/**
 * SECURITY ACTIVITY — every sign-in on your address, and every refusal.
 *
 * ── IT WAS GATED ON THE WRONG VARIABLE, AND ALWAYS SAID THERE WAS NOTHING ──
 *
 * The list drawn here is your audit entries. The empty message was decided by
 * `myWorkspaces` — how many workspaces you are in — and those two have nothing
 * to do with each other. Worse, `myWorkspaces` is filled by an effect that
 * returns early unless the ORGANISATION tab is the one open, so on this tab it
 * was empty every time. Opening Security Activity therefore said "Nothing
 * recorded yet" always, for everybody, however many entries existed — and
 * opening Organisation first and coming back made them appear, which is as
 * confusing a symptom as this could have had.
 *
 * Nothing was wrong with the recording. The audit table has been written the
 * whole time; this screen simply never showed it. That is worth saying because
 * the obvious guess — that the move to Postgres stopped the writing, as it did
 * for devices in #289 — is wrong here, and acting on it would have replaced a
 * working record for no reason.
 *
 * Split out for the same reason as DevicesPanel: it lives behind one of eight
 * tabs chosen by `useState`, and the screen harness cannot click, so inline it
 * could never be checked.
 */
/* The sizes on offer live in src/paging.ts now, shared with the board's
   Activity feed and with History — #310, "pages on both, keep it consistent". */

export function ActivityPanel({
  entries,
  problem,
  showAll = false,
}: {
  entries: AuditEntry[];
  problem: string;
  /** Draw everything on the first paint. For checks, which cannot press a button. */
  showAll?: boolean;
}) {
  /*
   * ── PAGES, REPLACING SHOW MORE — #310 ───────────────────────────────────
   *
   * ⚠️ THIS SUPERSEDES A DECISION MADE AN HOUR EARLIER THE SAME DAY. Walking
   * [[#294]] Vinnie said *"the cap is nice and then I can show more if need
   * be"* and Show more was recorded as settled. Told an hour later that pages
   * on the board's Activity feed would leave the two trails read by two
   * different mechanisms, he chose consistency: **"pages on both, keep it
   * consistent."** The note on #294 is not current; this is.
   *
   * The count and the cap stay. #294's fault was the SILENT cut, and only the
   * way on has changed.
   */
  const [size, setSize] = useState(showAll ? Number.MAX_SAFE_INTEGER : A_PAGE);
  const [page, setPage] = useState(1);
  const where = at(entries.length, size, page);
  const shown = entries.slice(where.start, where.end);
  const held = entries.length - shown.length;
  return (
    <>
      <h2>Security Activity</h2>
      {/*
        WHAT IT SAYS IT SHOWS IS WHAT IT SHOWS. This read "every sign-in on your
        address, and every time somebody using it was turned away" while drawing
        every kind of entry the person had ever caused — releases cut, cards
        deleted, comments removed. Vinnie, 26 August 2026, on being offered the
        narrower screen instead: "thats my audit trail, why do I want less info,
        i would want more if anything". So the list stays whole and the sentence
        was the thing that was wrong.
      */}
      <p className="hint">
        Everything recorded against your address — sign-ins, times somebody using it was turned
        away, and the changes you have made. Only you and an administrator can see this.
        {/*
          THE COUNT BELONGS BESIDE THE WORD IT QUALIFIES. A witness put it
          plainly on 26 August 2026: "Everything" sat above a list showing 50 of
          96, and the sentence saying so was fifty rows further down, out of
          sight. Not false — but not true of anything the reader could see at
          the moment they read it, which on an audit trail is the distinction
          that matters. The button stays at the bottom, where somebody runs out
          of rows; the count comes here, where the promise is made.
        */}
        {held > 0 && (
          <>
            {' '}
            <strong>
              Showing {where.from}–{where.to} of {entries.length}.
            </strong>
          </>
        )}
      </p>
      {/* Beside the sentence that makes the promise, which is where somebody
          decides how much of it they want to see at once. */}
      {entries.length > 0 && !showAll && <PageSize size={size} onSize={(n) => { setSize(n); setPage(1); }} />}
      {problem ? (
        <p className="hint act-problem">
          This list could not be read, so it is not safe to treat it as complete — {problem}. Try
          again in a moment; if it keeps happening, please report it.
        </p>
      ) : entries.length === 0 ? (
        <p className="hint">Nothing recorded yet.</p>
      ) : (
        <div className="act-list">
          {shown.map((e) => (
            <div key={e.id} className={`act-line ${e.kind === 'refused' ? 'is-refused' : ''}`}>
              <span className={`audit-kind audit-${e.kind}`}>{AUDIT_LABEL[e.kind]}</span>
              <span className="act-text">{e.what}</span>
              <span className="act-when">{historyTime(e.at)}</span>
            </div>
          ))}
        </div>
      )}
      {/*
        ⚠️ IT USED TO STOP AT FIFTY AND SAY NOTHING — #294. An address with 800
        entries showed fifty of them, with no count and no way on: 750 rows of
        somebody's own audit trail withheld in silence, on the screen that
        exists for them to check it. Worse on the busiest accounts, which are
        the ones most worth reading.

        A COUNT IS THE MINIMUM AND THE BUTTON IS THE POINT. Saying "50 of 831"
        alone would only tell somebody what they were being denied — the same
        dead end the board's activity feed still has, which is #296.
      */}
      <Pages total={entries.length} size={size} page={where.page} onPage={setPage} />

      {/* Somebody reading their own trail is the person best placed to
          notice a sign-in they did not make. Telling them what to do
          about it beats leaving them to work it out. */}
      <p className="hint">
        If anything here is not yours, change your password under Sign-In and Security and sign out
        of every device.
      </p>
    </>
  );
}

/**
 * THE PER-NOTIFICATION SWITCHES — Settings → Notifications (0126).
 *
 * "Tell me about" above sets the overall level; these turn individual kinds off.
 * Absence of a row means on, so the list starts all-on and only the mutes we read
 * back switch off. An action-required kind asks before it goes off, because a
 * sign-off you never hear about is a real cost — the warning is the friction, and
 * a kind an admin has LOCKED on cannot be switched off here at all.
 */
const NOTIFY_GROUPS: {
  group: string;
  action?: boolean;
  /* Each action-required kind carries its OWN warning: the cost of muting it is
     a different thing for each, and a safety prompt that names the wrong cost is
     worse than none. */
  items: { kind: string; name: string; what: string; warn?: string }[];
}[] = [
  {
    group: 'Updates',
    items: [
      { kind: 'mention', name: 'Someone mentioned you', what: 'When your name is tagged in a comment or description.' },
      { kind: 'reply', name: 'Someone replied to you', what: 'When someone replies to a comment you left.' },
      { kind: 'status_posted', name: 'A status update was posted', what: 'When someone posts an update on a card you’re assigned or a board you own.' },
      { kind: 'assigned', name: 'A card was assigned to you', what: 'When someone assigns a card to you.' },
      { kind: 'blocked', name: 'A card you own was blocked', what: 'When a card you own is marked blocked.' },
      { kind: 'due_soon', name: 'A due date is approaching', what: 'Shortly before a card assigned to you is due.' },
      { kind: 'cover_delegated', name: 'You were asked to cover someone', what: 'When you’re nominated to sign a teammate’s sign-offs while they’re away.' },
      { kind: 'cover_arranged', name: 'Cover was arranged for you', what: 'When an admin sets up a teammate to sign your sign-offs while you’re away.' },
    ],
  },
  {
    group: 'Approvals and decisions',
    action: true,
    items: [
      {
        kind: 'signoff_requested',
        name: 'A sign-off was requested of you',
        what: 'When you’re asked to sign off a document, sprint or release.',
        warn: 'You may miss a sign-off you’re required to give. With this off, FlightDeck won’t tell you — in the app or by email — when a document, sprint or release is waiting on you.',
      },
      {
        kind: 'signoff_decided',
        name: 'A decision was made on something you raised',
        what: 'When a sign-off you requested is answered.',
        warn: 'You may miss the outcome of a sign-off you asked for. With this off, FlightDeck won’t tell you — in the app or by email — when something you sent for sign-off is approved or rejected.',
      },
    ],
  },
];

function NotificationSwitches() {
  const load = useApp((s) => s.notificationSwitches);
  const mute = useApp((s) => s.muteKind);
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [locked, setLocked] = useState<Set<string>>(new Set());
  const [warn, setWarn] = useState<string | null>(null);
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let stop = false;
    void load().then((rows) => {
      if (stop) return;
      setMuted(new Set(rows.filter((r) => r.muted).map((r) => r.kind)));
      setLocked(new Set(rows.filter((r) => r.lockedBy).map((r) => r.kind)));
    });
    return () => {
      stop = true;
    };
  }, [load]);

  /* Escape backs out of the confirm, the way it closes every other menu and
     dialog in the app — the interaction-witness caught this one ignoring it.
     A document-level listener because focus stays on the toggle that opened it. */
  useEffect(() => {
    if (!warn) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWarn(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [warn]);

  /* Optimistic: flip the switch at once, roll back only if the save is refused —
     so a switch never sits half-pressed while a round-trip is in flight. */
  const setKind = async (kind: string, off: boolean) => {
    setProblem('');
    setMuted((m) => {
      const n = new Set(m);
      if (off) n.add(kind);
      else n.delete(kind);
      return n;
    });
    const err = await mute(kind, off);
    if (err) {
      setProblem(err);
      setMuted((m) => {
        const n = new Set(m);
        if (off) n.delete(kind);
        else n.add(kind);
        return n;
      });
    }
  };

  return (
    <>
      {NOTIFY_GROUPS.map((g) => (
        <div key={g.group}>
          <div className="grp-heading">{g.group}</div>
          {g.items.map((it) => {
            const isLocked = locked.has(it.kind);
            const on = isLocked || !muted.has(it.kind);
            return (
              <div key={it.kind} className={`feature-row ${on ? 'is-on' : ''}`}>
                <button
                  className={`switch ${on ? 'on' : ''}`}
                  role="switch"
                  aria-checked={on}
                  aria-label={it.name}
                  disabled={isLocked}
                  onClick={() => {
                    if (isLocked) return;
                    if (on) {
                      /* Turning an action-required kind off asks first; an
                         everyday one goes straight off. */
                      if (g.action) setWarn(it.kind);
                      else void setKind(it.kind, true);
                    } else {
                      void setKind(it.kind, false);
                    }
                  }}
                >
                  <span className="switch-knob" />
                </button>
                <div className="feature-main">
                  <div className="feature-name">
                    {it.name}
                    {g.action && <span className="req-mark">Needs your action</span>}
                  </div>
                  <p className="feature-what">{it.what}</p>
                  {isLocked && <p className="feature-what feature-locked">Kept on by an admin.</p>}
                  {warn === it.kind && (
                    <div className="notif-warn">
                      <div className="notif-warn-top">
                        <Icon name="warning" size={16} />
                        <p>{it.warn}</p>
                      </div>
                      <div className="notif-warn-act">
                        <button
                          className="btn btn-warn"
                          onClick={() => {
                            setWarn(null);
                            void setKind(it.kind, true);
                          }}
                        >
                          Turn it off anyway
                        </button>
                        <button className="btn btn-primary" onClick={() => setWarn(null)}>
                          Keep it on
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {problem && (
        <p className="sg-problem" role="alert">
          {problem}
        </p>
      )}
    </>
  );
}
